import { Component, OnInit } from '@angular/core';
import { MoreButtonComponent } from '../../../core/buttons/more-button.component';
import { ClaimLabelComponent } from '../../../core/labels/claim-label.component';
import { TagComponent, TagType } from '../../../core/labels/tag.component';
import { ActionRowComponent } from '../../../core/panels/action-row.component';
import { DetailItemComponent } from '../../../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../../../core/panels/detail-panel/detail-panel.component';
import { BaseComponent } from '../../../base/base.component';
import { ClaimRow } from '../../../../models/api/view/claim-view/claim-row';
import { ClaimGLClaimCard } from '../../../../models/api/view/claim/summary/claim-gl-claim-card';
import {
  LoadingPanelComponent,
  LoadingState,
} from '../../../core/panels/loading-panel.component';
import { ClaimFlagsComponent } from './claim-flags.component';
import { HandleLoadingState } from '../../../../decorators/handle-loading-state.decorator';
import { getFieldVisibility } from '../../../../functions/field-security';

@Component({
  selector: 'app-pr-claim-card',
  imports: [
    DetailPanelComponent,
    DetailItemComponent,
    TagComponent,
    ActionRowComponent,
    MoreButtonComponent,
    ClaimLabelComponent,
    LoadingPanelComponent,
    ClaimFlagsComponent,
  ],
  template: `
    <app-loading-panel
      [state]="loadingState"
      [loadingHeight]="300"
      loadingStyle="Box"
      [error]="error"
    >
      <ng-template #loaded>
        @if (prClaimCard) {
          <div class="tw-w-full">
            <div
              class="tw-flex tw-flex-col md:tw-flex-row tw-gap-0 md:tw-gap-8 tw-w-full"
            >
              <app-detail-panel [dataSource]="prClaimCard" class="tw-w-full ">
                <app-detail-item label="Claim Number" name="claimNumber">
                  <ng-template let-data #value>
                    <app-claim-label [claim]="claimRow" [readOnly]="true" />
                  </ng-template>
                </app-detail-item>
                <app-detail-item
                  label="Status"
                  [ariaLabel]="'Status: ' + statusDisplay"
                >
                  <ng-template let-data #value>
                    <app-tag [type]="statusType">{{ statusDisplay }}</app-tag>
                  </ng-template>
                </app-detail-item>
                <app-detail-item
                  label="Date of Loss"
                  name="dateOfLoss"
                  format="dt"
                />
                <app-detail-item label="Jurisdiction" name="jurisdiction" />
              </app-detail-panel>

              <app-detail-panel [dataSource]="injuryData" class="tw-w-full">
                @if (visibility?.showCause) {
                  <app-detail-item label="Cause" name="cause" />
                }
                @if (visibility?.showNatureResult) {
                  <app-detail-item
                    label="Nature/result"
                    name="nature"
                    labelCase="none"
                  />
                }
                @if (visibility?.showPartTarget) {
                  <app-detail-item
                    label="Part/target"
                    name="part"
                    labelCase="none"
                  />
                }
                @if (prClaimCard.showBodySide) {
                  <app-detail-item
                    label="Body Side"
                    name="bodySide"
                    labelCase="none"
                  />
                }
              </app-detail-panel>
            </div>

            @if (
              visibility?.showEventDescription && prClaimCard.eventDescription
            ) {
              <div>
                <div class="tw-font-bold tw-py-2">Event description</div>
                <div class="tw-pb-4 tw-whitespace-pre-line">
                  {{ prClaimCard.eventDescription }}
                </div>
              </div>
            }

            <app-action-row>
              <app-claim-flags left1 [flags]="prClaimCard" />
              @if (this.propertyToken) {
                <app-more-button
                  right1
                  (click)="this.view.navigateCurrentClaim(['property'])"
                  [ariaLabel]="
                    'Navigate to Property Details for ' +
                    prClaimCard?.claimNumber
                  "
                />
              }
            </app-action-row>
          </div>
        }
      </ng-template>
    </app-loading-panel>
  `,
  styles: ``,
})
export class PrClaimCardComponent extends BaseComponent implements OnInit {
  claimRow: ClaimRow;
  prClaimCard: ClaimGLClaimCard;
  propertyToken = false;
  statusType: TagType = 'Success';
  statusDisplay = '';
  loadingState: LoadingState = 'Loading';
  injuryData: any = {};
  error: any = null;
  visibility: ReturnType<typeof getFieldVisibility>;

  @HandleLoadingState({
    loadingProperty: 'loadingState',
    onValue: 'Loading',
    offValue: 'Loaded',
  })
  async ngOnInit(): Promise<void> {
    this.claimRow = this.view.currentClaim();
    this.propertyToken = this.session.authorize('Property');

    this.prClaimCard = await this.api.claimSummary.getGLClaimCard(
      this.claimRow.recordSource,
      this.claimRow.recordId,
    );

    if (this.prClaimCard) {
      this.prClaimCard.showBodySide = this.prClaimCard.coverageCode === 'OT';

      if (this.needsPrStatusFallback(this.prClaimCard)) {
        await this.enrichPrStatusFromClaimHeader();
      }

      this.applyPrStatusDisplay();

      this.visibility = getFieldVisibility(
        this.session.user?.fieldSecurity?.restrictedFields,
      );

      this.injuryData = {
        cause: this.prClaimCard.cause || '',
        nature: this.prClaimCard.natureResult || '',
        part: this.prClaimCard.partTarget || '',
        bodySide: this.prClaimCard.bodySide || '',
      };
    }
  }

  /** Same display rules as AU claim card (reference implementation). */
  private applyPrStatusDisplay(): void {
    this.statusDisplay =
      this.prClaimCard.status +
      (this.prClaimCard.subStatus ? ' - ' + this.prClaimCard.subStatus : '');
    this.statusType = this.getStatusType(this.prClaimCard.status);
  }

  /**
   * PR-only: GL claim-card response can omit status when list/search columns omit it.
   * Claim header + summary info match legacy and are independent of search layout.
   */
  private needsPrStatusFallback(card: ClaimGLClaimCard): boolean {
    const s = card?.status;
    if (s == null || String(s).trim() === '') return true;
    if (String(s).toLowerCase() === 'undefined') return true;
    return false;
  }

  private async enrichPrStatusFromClaimHeader(): Promise<void> {
    const header = await this.api.claimHeader.get({
      recordSource: this.claimRow.recordSource,
      recordId: this.claimRow.recordId,
    });
    if (!header?.status?.trim()) return;

    const info = await this.api.claimSummary.getClaimSummaryInfo(
      header.recordSource,
      header.recordId,
    );
    let subStatus = header.subStatus ?? '';
    let subReason = header.subReason ?? '';
    if (info?.claimSubStatus) subStatus = info.claimSubStatus;
    if (info?.claimSubReason) subReason = info.claimSubReason;
    const sub = subStatus || subReason || '';

    this.prClaimCard.status = header.status;
    this.prClaimCard.subStatus = sub;
  }

  private getStatusType(status: string): TagType {
    const statusUpper = status?.toUpperCase() || '';
    if (statusUpper.includes('OPEN') || statusUpper.includes('REOPEN')) {
      return 'Success';
    }
    if (statusUpper.includes('CLOSED')) {
      return 'Error';
    }
    if (statusUpper.includes('INCIDENT')) {
      return 'Warn';
    }
    return 'Success';
  }
}
